import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, MessageCircle, Crown, Heart, Gem, GraduationCap, Headphones, Bus, Brain, Map as MapIcon } from 'lucide-react';
import { LanguageSelector } from './LanguageSelector';
import { ShareButtons } from './ShareButtons';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
export const Footer = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const {
    language
  } = useLanguage();
  const t = translations[language];
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <footer className="bg-black text-white relative overflow-hidden" data-id="element-1240">
      <div className="absolute inset-0 bg-gradient-to-t from-amber-900/10 to-transparent opacity-20" data-id="element-1241"></div>
      <div className="container mx-auto px-4 py-20 relative z-10" data-id="element-1242">
        {/* Language Selector */}
        <div className="flex justify-center mb-12" data-id="element-1243">
          <LanguageSelector data-id="element-1244" />
        </div>
        {/* Share Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-2xl mx-auto mb-20 border border-amber-400/30 p-8 bg-black/40" data-id="element-1245">
          <ShareButtons layout="horizontal" title="Share This Cultural Experience" data-id="element-1246" />
        </motion.div>
        {/* FAQ Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-4xl mx-auto mb-20" data-id="element-1247">
          <h4 className="text-center font-serif text-3xl text-amber-400 mb-12 tracking-wider uppercase" data-id="element-1248">
            {t.faqTitle}
          </h4>
          <div className="space-y-4" data-id="element-1249">
            {t.faqQuestions.map((faq, index) => <div key={index} className="border border-amber-400/30 bg-black/40 overflow-hidden" data-id="element-1250">
                <button onClick={() => toggleFaq(index)} className="flex justify-between items-center w-full p-6 text-left" data-id="element-1251">
                  <span className="text-white font-light pr-4" data-id="element-1252">
                    {faq.question}
                  </span>
                  {openFaq === index ? <ChevronUp className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-1253" /> : <ChevronDown className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-1254" />}
                </button>
                {openFaq === index && <div className="px-6 pb-6 text-white/70 font-light leading-relaxed border-t border-amber-400/20 pt-4" data-id="element-1255">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </motion.div>
        {/* Footer Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16 border-t border-amber-400/30 pt-16" data-id="element-1256">
          {/* Brand */}
          <div className="md:col-span-2" data-id="element-1257">
            <div className="flex items-center mb-6" data-id="element-1258">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/a1thpmaUHZUisJ3HgEhAjA/oldkhanda.png" alt="Khanda Symbol" className="w-10 h-10 mr-3 object-contain" data-id="element-1259" />
              <h3 className="font-serif text-2xl text-amber-400" data-id="element-1260">
                {t.footerTitle}
              </h3>
            </div>
            <p className="text-white/60 mb-6 max-w-md font-light leading-relaxed" data-id="element-1261">
              {t.footerDescription}
            </p>
          </div>
          {/* Quick Links */}
          <div data-id="element-1262">
            <h4 className="text-amber-400 font-light text-sm mb-4 tracking-[0.3em] uppercase" data-id="element-1263">
              {t.quickLinks}
            </h4>
            <ul className="space-y-3" data-id="element-1264">
              {t.links.map((link, index) => <li key={index} data-id="element-1265">
                  <a href={link.href} className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light" data-id="element-1266">
                    {link.text}
                  </a>
                </li>)}
              <li data-id="element-1267">
                <a href="/blueprint" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light" data-id="element-1268">
                  Experience Blueprint
                </a>
              </li>
              <li data-id="element-1269">
                <a href="/kingdom-circle" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1270">
                  <Crown className="w-3 h-3" data-id="element-1271" />
                  Kingdom Circle
                </a>
              </li>
              <li data-id="element-1272">
                <a href="/sikh-heritage-forward" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1273">
                  <Heart className="w-3 h-3" data-id="element-1274" />
                  Sikh Heritage Forward
                </a>
              </li>
              <li data-id="element-1275">
                <a href="/treasure" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1276">
                  <Gem className="w-3 h-3" data-id="element-1277" />
                  Treasures of the Sikh Empire
                </a>
              </li>
              <li data-id="element-1278">
                <a href="/restore-punjab" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1279">
                  <MapIcon className="w-3 h-3" data-id="element-1280" />
                  Restore Punjab
                </a>
              </li>
              <li data-id="element-1281">
                <a href="/schools" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1282">
                  <GraduationCap className="w-3 h-3" data-id="element-1283" />
                  For Schools &amp; Curriculum Partners
                </a>
              </li>
              <li data-id="element-1284">
                <a href="/sponsor-a-bus" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1285">
                  <Bus className="w-3 h-3" data-id="element-1286" />
                  Sponsor a Bus
                </a>
              </li>
              <li data-id="element-1287">
                <a href="/house-of-lions" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1288">
                  <Headphones className="w-3 h-3" data-id="element-1289" />
                  House of Lions Podcast
                </a>
              </li>
              <li data-id="element-1290">
                <a href="/ai-entrepreneurship" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1291">
                  <Brain className="w-3 h-3" data-id="element-1292" />
                  Entrepreneurship and AI Opportunity
                </a>
              </li>
              <li data-id="element-1293">
                <a href="/sponsor-deck" className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light flex items-center gap-2" data-id="element-1294">
                  <Crown className="w-3 h-3" data-id="element-1295" />
                  Sponsor Deck
                </a>
              </li>
            </ul>
          </div>
          {/* Contact */}
          <div data-id="element-1296">
            <h4 className="text-amber-400 font-light text-sm mb-4 tracking-[0.3em] uppercase" data-id="element-1297">
              {t.contactUs}
            </h4>
            <a href="https://chat.whatsapp.com/Joakmqmpdd3A0riGDdBEEb?mode=wwc" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-black font-medium py-3 px-6 transition-colors text-sm" data-id="element-1298">
              <MessageCircle className="w-4 h-4" data-id="element-1299" />
              {t.subscribe}
            </a>
          </div>
        </div>
        {/* Logo & Copyright */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center border-t border-amber-400/30 pt-12" data-id="element-1300">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex justify-center mb-8" data-id="element-1301">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aqXvi2nxLJFgni6jP9pU4X/image.png" alt="Sikh Kingdoms Logo" className="w-32 h-32 object-cover rounded-full opacity-80" data-id="element-1302" />
          </motion.div>
          <p className="text-white/40 text-xs tracking-wider uppercase font-light" data-id="element-1303">
            {t.copyright}
            <br data-id="element-1304" />
            {t.allRightsReserved}
          </p>
        </motion.div>
      </div>
    </footer>;
};